import React, { useState } from 'react';
import { Typography, Container, Button, Breadcrumbs } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import FeatureShowcase from '../shared-components/FeatureShowcase';
import FeatureCard from '../shared-components/FeatureCard';
import CTASection from '../shared-components/CTASection';
import { NavigateBefore } from '@mui/icons-material';
import { getRelatedFeatures } from './featuresData';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';

function FeatureDetail({ feature }) {
  const navigate = useNavigate();
  const [imageOpen, setImageOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);

  const openImage = (imageSrc) => {
    setCurrentImage(imageSrc);
    setImageOpen(true);
  };
  const pillarColors = {
    operations: { bg: 'bg-blue-100', text: 'text-blue-700' },
    network: { bg: 'bg-green-100', text: 'text-green-700' },
    care: { bg: 'bg-purple-100', text: 'text-purple-700' }
  };

  // Handle both single pillar and multi-pillar features
  const featurePillars = feature.pillars || [feature.pillar];
  const relatedFeatures = getRelatedFeatures(feature.relatedFeatures);

  return (
    <div className="min-h-screen">
      {/* HERO SECTION */}
      <section className="pt-8 pb-20 lg:pb-32 bg-gradient-to-br from-blue-50 via-white to-white">
        <Container maxWidth="lg">
          <div className="mb-0">
            <Button
              component={Link}
              to="/features"
              startIcon={<NavigateBefore />}
              className="text-blue-600 hover:text-blue-800 font-semibold"
            >
              All Features
            </Button>
          </div>
          
          <div className="text-center max-w-4xl mx-auto">
            <Typography
              variant="h1"
              align="center"
              className="text-4xl lg:text-6xl font-extrabold text-gray-900 mb-4 leading-tight"
            >
              {feature.title}
            </Typography>
            
            <div className="flex flex-wrap justify-center gap-2 mb-6">
              {featurePillars.map(pillar => (
                <div key={pillar} className={`inline-block px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wide ${pillarColors[pillar]?.bg || 'bg-blue-100'} ${pillarColors[pillar]?.text || 'text-blue-700'}`}>
                  {pillar}
                </div>
              ))}
            </div>

            <Typography
              variant="h2"
              align="center"
              className="text-xl lg:text-2xl text-blue-600 mb-6 font-medium italic leading-relaxed"
            >
              {feature.tagline}
            </Typography>

            <Typography
              variant="body1"
              align="center"
              className="text-lg text-gray-600 mb-12 leading-relaxed"
            >
              {feature.shortDescription}
            </Typography>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                component={Link}
                to="/sign-up"
                variant="contained"
                size="large"
                className="bg-blue-600 hover:bg-blue-700 px-8 py-3.5 text-base font-bold shadow-lg shadow-blue-500/30 transition-all hover:-translate-y-0.5"
              >
                Get Started Free
              </Button>
              <Button
                onClick={() => navigate(-1)}
                variant="outlined"
                size="large"
                className="border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 px-8 py-3.5 text-base font-semibold"
              >
                Go Back
              </Button>
            </div>
          </div>
        </Container>
      </section>

      {/* FEATURE DEEP DIVE */}
      <section className="py-24 bg-gray-50">
        <Container maxWidth="lg">
          <div className="text-center mb-20 w-full max-w-3xl mx-auto">
            <Typography variant="h2" align="center" className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              How It Works
            </Typography>
            <Typography variant="body1" align="center" className="text-xl text-gray-600 leading-relaxed">
              Explore the key capabilities that make {feature.title} powerful.
            </Typography>
          </div>

          <div className="space-y-36">
            {feature.showcases.map((showcase, index) => (
              <FeatureShowcase
                key={index}
                title={showcase.title}
                description={showcase.description}
                stat={showcase.stat}
                imagePosition={showcase.imagePosition}
                imageFile={showcase.imageFile}
                onImageClick={openImage}
              />
            ))}
          </div>
        </Container>
      </section>

      {/* USE CASES */}
      <section className="py-24 bg-white">
        <Container maxWidth="lg">
          <div className="text-center mb-16 w-full max-w-3xl mx-auto">
            <Typography variant="h2" align="center" className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Use Cases
            </Typography>
            <Typography variant="body1" align="center" className="text-xl text-gray-600 leading-relaxed">
              See how mental health professionals use {feature.title} in their practice.
            </Typography>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {feature.useCases.map((useCase, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-md border border-gray-100 hover:shadow-lg transition-shadow">
                <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <Typography className="text-2xl font-bold text-white">
                    {index + 1}
                  </Typography>
                </div>
                <Typography variant="h6" className="font-bold text-gray-900 mb-4 text-center">
                  {useCase.title}
                </Typography>
                <Typography variant="body1" className="text-gray-600 leading-relaxed text-center">
                  {useCase.description}
                </Typography>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* RELATED FEATURES */}
      <section className="py-24 bg-gray-50">
        <Container maxWidth="lg">
          <div className="text-center mb-16 w-full max-w-3xl mx-auto">
            <Typography variant="h2" align="center" className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Related Features
            </Typography>
            <Typography variant="body1" align="center" className="text-xl text-gray-600 leading-relaxed">
              Explore other features that work seamlessly with {feature.title}.
            </Typography>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {relatedFeatures.map(relatedFeature => (
              <FeatureCard
                key={relatedFeature.id}
                icon={<relatedFeature.icon sx={{ fontSize: 40 }} />}
                title={relatedFeature.title}
                description={relatedFeature.shortDescription}
                pillars={relatedFeature.pillars}
                slug={relatedFeature.slug}
              />
            ))}
          </div>
        </Container>
      </section>

      {/* FINAL CTA SECTION */}
      <CTASection
        headline={`Ready to Use ${feature.title}?`}
        copy="Join other mental health professionals using Halespring to reduce admin time and improve patient outcomes."
        primaryCTA="Get Started Free"
        secondaryCTA="View Pricing"
        trustElements={[
          "Free account - no card needed",
          "Premium trial available",
          "HIPAA BAA included",
          "Cancel anytime"
        ]}
      />

      {/* Image Lightbox */}
      <Lightbox
        open={imageOpen}
        close={() => setImageOpen(false)}
        slides={currentImage ? [{ src: currentImage }] : []}
      />
    </div>
  );
}

export default FeatureDetail;
